import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaBuilding, FaStore, FaCrown } from "react-icons/fa";
import { ListSkeleton, EmptyState, ListView, ListToolbar } from "../../shared";
import FirmCard from "../FirmCard";
import { Pagination } from "../../atoms";

// Bayiler görünümünün filtre sekmeleri. "Tümü" varsayılan; FilterTabs
// toggle-off desteklemediği için "show all" durumu bu sekmeyle sağlanır.
const DEALER_FILTERS = [
  { id: "all", label: "Tümü", icon: FaBuilding },
  { id: "anaBayi", label: "Ana Bayi", icon: FaCrown },
  { id: "bayi", label: "Bayilerim", icon: FaStore },
];

// variant → o görünümde listelenecek dealerRelation değerleri (baz kapsam)
const VARIANT_RELATIONS = {
  dealers: ["anaBayi", "bayi"],
  customers: ["musteri"],
};

// variant → görünüme özel metinler ("Firma" jenerik ifadesi yerine)
const VARIANT_LABELS = {
  dealers: { searchPlaceholder: "Bayi Adı", empty: "Bayi bulunamadı", entityHeader: "BAYİ BİLGİLERİ" },
  customers: { searchPlaceholder: "Müşteri Adı", empty: "Müşteri bulunamadı", entityHeader: "MÜŞTERİ BİLGİLERİ" },
};

const getTableHeaders = (variant) => [
  { label: VARIANT_LABELS[variant]?.entityHeader || "FİRMA BİLGİLERİ", width: "w-[30%]", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "KOTALAR", width: "w-[240px] max-w-[240px]", skeletonWidth: "w-20", skeletonExtra: true },
  { label: "ANA FİRMA", width: "w-[14%]", skeletonWidth: "w-24" },
  { label: "BAĞLI BAYİLER", width: "w-[14%]", skeletonWidth: "w-24" },
  { label: "İLETİŞİM", width: "w-[24%]", skeletonWidth: "w-24", skeletonExtra: true },
];

function DealerFirmList({
  firmsData = [],
  isLoading,
  canNavigate = false,
  searchParams = {},
  onSearchParamsChange = {},
  variant = "dealers",
  // Satır sonu işlem hücresi (#303 — Müşterilerim'de Kota Ver / Al).
  renderActions,
  // Kilidi tutulan (işlemi süren) firma id'leri — "İşlem sürüyor" rozeti.
  busyIds,
}) {
  const baseRelations = VARIANT_RELATIONS[variant] || VARIANT_RELATIONS.dealers;
  const labels = VARIANT_LABELS[variant] || VARIANT_LABELS.dealers;
  const showActionsColumn = typeof renderActions === "function";
  const tableHeaders = useMemo(
    () => [
      ...getTableHeaders(variant),
      ...(showActionsColumn
        ? [{ label: "İŞLEM", width: "w-[150px]", skeletonWidth: "w-20" }]
        : []),
    ],
    [variant, showActionsColumn]
  );
  const cardProps = (firm) => ({
    busy: !!busyIds && busyIds.has(String(firm._id)),
    actions: showActionsColumn ? renderActions(firm) : null,
    showActionsColumn,
  });
  // Müşteriler görünümünde tek tip listelenir; filtre sekmesi gösterilmez.
  const showFilters = variant === "dealers";
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasTyped, setHasTyped] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const hasCalculatedLimitRef = useRef(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (hasCalculatedLimitRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      if (hasCalculatedLimitRef.current) return;
      const { height } = entries[0]?.contentRect || {};
      if (height && height > 80) {
        const rowHeight = 56;
        const count = Math.max(5, Math.floor(height / rowHeight));
        setItemsPerPage(count);
        hasCalculatedLimitRef.current = true;
        ro.disconnect();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const filteredFirms = useMemo(() => {
    return firmsData.filter((firm) => {
      if (!firm) return false;
      // Görünümün baz kapsamı (Bayiler: ana bayi + bayi; Müşteriler: müşteri)
      if (!baseRelations.includes(firm.dealerRelation)) return false;
      // Görünüm içi sekme filtresi (sadece Bayiler görünümünde anlamlı)
      if (showFilters && activeFilter !== "all" && firm.dealerRelation !== activeFilter) return false;
      return true;
    });
  }, [firmsData, activeFilter, baseRelations, showFilters]);

  const paginatedFirms = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredFirms.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredFirms, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredFirms.length / itemsPerPage);
  const prevTotalPagesRef = useRef(totalPages);
  useEffect(() => {
    if (totalPages > 0) prevTotalPagesRef.current = totalPages;
  }, [totalPages]);
  const displayTotalPages = totalPages > 0 ? totalPages : prevTotalPagesRef.current;

  useEffect(() => { setCurrentPage(1); }, [activeFilter, searchParams]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  };

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setHasTyped(true);
  };

  const handleSearch = (setter) => (e) => {
    setHasTyped(true);
    setter?.(e.target.value);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="bg-white flex-1 min-h-0 flex flex-col">
        <ListToolbar
          className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
          mobileFilterSlotId="firms-toolbar-mobile-slot"
          filterTabs={
            showFilters
              ? {
                  filters: DEALER_FILTERS,
                  activeFilter,
                  onChange: handleFilterChange,
                  totalCount: filteredFirms.length,
                  isLoading,
                  color: "blue",
                }
              : undefined
          }
          searchFields={[
            {
              placeholder: labels.searchPlaceholder,
              value: searchParams.firmName || "",
              onChange: handleSearch(onSearchParamsChange.setFirmName),
            },
            {
              placeholder: "Bayser no",
              value: searchParams.bayserNo || "",
              onChange: handleSearch(onSearchParamsChange.setBayserNo),
            },
          ]}
          searchNote={
            hasTyped && isLoading ? (
              <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-600">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>Aranıyor...</span>
              </div>
            ) : null
          }
        />

        <ListView
          contentRef={contentRef}
          headers={tableHeaders.map((h) => ({ label: h.label, className: h.width }))}
          tableClassName="table-fixed"
          data={paginatedFirms}
          rowKey={(firm, i) => firm._id || firm.id || i}
          renderRow={(firm) => (
            <FirmCard firm={firm} canNavigate={canNavigate} {...cardProps(firm)} />
          )}
          renderMobileCard={(firm) => (
            <FirmCard
              firm={firm}
              canNavigate={canNavigate}
              isMobileView={true}
              {...cardProps(firm)}
            />
          )}
          isLoading={isLoading}
          loadingNode={
            <ListSkeleton columns={tableHeaders} rowCount={20} mobileCardCount={6} />
          }
          empty={
            <div className="p-4 md:p-6">
              <EmptyState message={labels.empty} />
            </div>
          }
          footer={
            filteredFirms.length > 0 || isLoading ? (
              <div className="shrink-0 border-t border-gray-200 bg-white">
                <Pagination
                  currentPage={currentPage}
                  totalPages={isLoading && totalPages === 0 ? displayTotalPages : totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredFirms.length}
                  showInfo={true}
                  disabled={isLoading}
                />
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}

export default DealerFirmList;
